import styled, { useTheme } from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import Banners from '../../components/Banners';
import Rewards from '../../components/Rewards';
import {
  Heading3Text,
  Heading4Text,
  ParagraphMediumText,
  ParagraphSmallText,
} from '../../components/Texts';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import Button from '../../components/Button';
import RewardsTable from './Table';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/slices/modals';
import { useGetCurrentUserReferralsQuery } from '../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../redux/slices/auth';
import { formatUSDEnsure2Digits } from '../../common/currency';
import ScrollRestore from '../../components/ScrollRestore';
import { usePrivy } from '@privy-io/react-auth';
import useCustomLogin from '../../hooks/useCustomLogin';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 20px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const StyledHeading4Text = styled(Heading4Text)`
  font-weight: 600;
  font-size: 28px;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 12px;
  }
`;

const Info = styled.div`
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 12px;
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const InfoText = styled(ParagraphSmallText)`
  text-align: center;
  color: ${({ theme }) => theme.text.secondary};
`;

const ActivitySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const ActivityTitle = styled(Heading4Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border.primary};
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border.primary};
`;

const EmptyStateText = styled(ParagraphSmallText)`
  color: ${({ theme }) => theme.text.secondary};
`;

const RewardsView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { login } = useCustomLogin();
  const { user } = usePrivy();
  const privyToken = useSelector(selectPrivyToken);
  const { data: referrals, isLoading: isLoadingReferrals } =
    useGetCurrentUserReferralsQuery({
      skip: !privyToken,
    });

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  const hasActivity = referrals?.history && referrals.history.length > 0;

  return (
    <Container>
      <ScrollRestore />
      <TitleContainer>
        <StyledHeading4Text style={{ color: theme.text.title }}>
          {t('rewardsPage.title')}
        </StyledHeading4Text>
        <ParagraphMediumText style={{ color: theme.text.regular }}>
          <Trans i18nKey="rewardsPage.subtitle" components={{ b: <b /> }} />
        </ParagraphMediumText>
      </TitleContainer>
      <CardsContainer>
        <Info>
          <ParagraphMediumText style={{ color: theme.text.regular }}>
            {t('rewardsPage.cashbackEarned')}
          </ParagraphMediumText>
          <Heading3Text style={{ color: theme.status.success.primary }}>
            {formatUSDEnsure2Digits(referrals?.totalReferralEarnings || 0)}
          </Heading3Text>
        </Info>
        <Info>
          <ParagraphMediumText style={{ color: theme.text.regular }}>
            {t('rewardsPage.friendsInvited')}
          </ParagraphMediumText>
          <Heading3Text style={{ color: theme.text.title }}>
            {referrals?.referredUsersCount || 0}
          </Heading3Text>
        </Info>
      </CardsContainer>
      <ButtonContainer>
        <Button
          onClick={() => {
            if (!user) {
              login();
            } else {
              dispatch(openModal({ name: 'referrals' }));
            }
          }}
        >
          {t('rewardsPage.inviteFriend')}
        </Button>
        <InfoText>
          {t('rewardsPage.earnCashbackHint')}
        </InfoText>
      </ButtonContainer>
      <ActivitySection>
        <ActivityTitle>{t('rewardsPage.activity')}</ActivityTitle>
        {!hasActivity && !isLoadingReferrals ? (
          <EmptyStateContainer>
            <EmptyStateText>{t('rewardsPage.noCashbackYet')}</EmptyStateText>
            <EmptyStateText>
              {t('rewardsPage.noCashbackYetHint')}
            </EmptyStateText>
          </EmptyStateContainer>
        ) : (
          <RewardsTable
            data={referrals?.history || []}
            isLoading={isLoadingReferrals}
          />
        )}
      </ActivitySection>
    </Container>
  );
};

export default RewardsView;
